// frontend/src/Components/SyncTo/ReviewModal.jsx
//
// Phase 32 — Review/Apply gate for Note-mode SyncTo.
// Phase 33 — EXTENDED with Template variant (mode === 'template').
//
// NOTE VARIANT (Phase 32 — unchanged):
//   Parent (RecordingProvider) opens the modal after /transcribe succeeds,
//   passing the cleaned Speaker 1:/Speaker 2: paragraphs as props. User edits the
//   textarea, optionally flips speaker labels, then clicks Apply — which POSTs
//   to /api/sync-to/apply via useSyncToApi.apply().
//
// TEMPLATE VARIANT (Phase 33 — NEW):
//   Parent opens the modal after /transcribe Template-branch succeeds, passing
//   routedValues (AI-proposed per-element values), dropped (elements absent
//   from AI response), existingCapturedValues (session's current state), and
//   templatePages (pages[] for label/type lookup + NoteRenderer preview).
//   Modal renders a per-element review table with Accept checkboxes. Below
//   the table a <details> pane shows NoteRenderer in preview mode with the
//   merged capturedValues overlay ({ ...existing, ...acceptedProposed }) so
//   the doctor sees exactly what the session will look like after Apply.
//   PITFALL #12 invariant: NoteRenderer's readonlyValues overlay is read-only
//   for the preview — the real mutation happens server-side in /apply.
//
// Invariants:
//   - Does NOT manage show internally; parent controls open/close.
//   - Does NOT call onClose on Apply-success; parent handles close via onApplied.
//   - Speaker-swap (Note variant) operates on the TEXTAREA CURRENT VALUE (D-09)
//     — preserves user inline edits. __SYNCTO_TMP__ sentinel is clinically impossible.
//   - No PHI logging — zero console.* calls in this file (T-32-27 / T-33-17).
//   - Renders via Form.Control as=textarea / plain text nodes (no innerHTML) —
//     no unsafe HTML injection (T-32-22 / T-33-16).
//   - Template variant: acceptedFields submitted to /apply contains ONLY rows
//     where Accept checkbox is checked (T-33-18 — unchecked rows excluded
//     client-side; server call #2 re-validates regardless).

import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Form } from 'react-bootstrap';
import { useSyncToApi } from './hooks/useSyncToApi';
import { getAppointmentsByPatientId } from '../../api';
import NoteRenderer from '../Templates/NoteRenderer';
import AppToast from '../Toasts/AppToast';
import { useToast } from '../Toasts/useToast';
import ConfirmModal from '../Confirm/ConfirmModal';
import { useConfirm } from '../Confirm/useConfirm';

// Sentinel used during dropdown-driven label replacement. Chosen to be
// clinically impossible (double-underscore identifier is not a valid medical
// token in en-ZA or af-ZA clinical corpus).
const SPEAKER_SWAP_SENTINEL = '__SYNCTO_TMP__';

// Backend prompt emits "Speaker 1:" / "Speaker 2:" prefixes; user picks roles
// here. Dropdown options match the prefix labels we replace in the textarea.
const SPEAKER_LABEL_OPTIONS = ['Practitioner', 'Patient', 'Other'];

// D-10 banner copy — locked to the exact string from CONTEXT.md specifics.
const CLEANUP_FAILED_TEXT =
  'Auto-cleanup unavailable — please add speaker labels and review carefully before applying.';

// D-06 idempotent info banner.
const IDEMPOTENT_TEXT = 'Already applied — no change.';

// Phase 34 SC7 — flag-flipped-off-mid-session banner copy (locked literal —
// matches ROADMAP Phase 34 Success Criterion #7 verbatim). Surfaced when the
// /apply endpoint returns 403 with the exact requireSyncToFeatureFlag message.
const FEATURE_DISABLED_TEXT = 'This feature has been disabled for your account.';
const FEATURE_DISABLED_STATUS = 403;
const FEATURE_DISABLED_MESSAGE = 'SyncTo is not enabled for this user';

/** "01 Sep 2026, 13:00 — Completed", from the appointments/patient shape. */
function formatAppointmentOption(a) {
  const when = a?.startDate
    ? new Date(`${a.startDate}T${a.time || '00:00'}`)
    : null;
  const label = when && !Number.isNaN(when.getTime())
    ? when.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Unknown date';
  return a?.status ? `${label} — ${a.status}` : label;
}

// Phase 33 — allowlist for Template variant. Matches backend contract:
// validateRoutedValues in syncToService.js accepts ONLY these types. All other
// template element types (radio, yes_no, numeric, pain_scale, dropdown, date,
// image, photo_upload, title, divider, static_text, spacer) are rendered as
// greyed "Not routed — please fill manually (type: <type>)" rows.
const TEMPLATE_ALLOWLIST_TYPES = ['text', 'memo', 'rich_memo', 'checkbox'];

/**
 * Detect speakers present in a transcript by scanning for `Speaker N:` /
 * `Practitioner:` / `Patient:` prefixes at line starts. Returns sorted unique
 * speaker numbers found from `Speaker N:` matches; the corresponding
 * current label is whatever prefix is currently in the text for that speaker.
 *
 * `Doctor` is kept in the regex for backward compatibility — older saved
 * transcripts may still contain `Doctor:` line prefixes from before the rename.
 */
function detectSpeakers(text) {
  if (typeof text !== 'string') return [];
  const seen = new Map(); // speakerNum -> currentLabel
  const re = /^(Speaker\s+(\d+)|Practitioner|Doctor|Patient|Other):/gm;
  let m;
  while ((m = re.exec(text)) !== null) {
    const label = m[1];
    // Use the position-derived speaker number when available; fall back to
    // the label itself as the key so renamed prefixes still register.
    const num = m[2] ? parseInt(m[2], 10) : null;
    if (num !== null && !seen.has(num)) {
      seen.set(num, label);
    }
  }
  return Array.from(seen.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([num, currentLabel]) => ({ num, currentLabel }));
}

/**
 * Replace every reference to one speaker label with another using a sentinel
 * pass to avoid collisions when the new label could itself appear elsewhere.
 *
 * Two forms are replaced:
 *   1. `OldLabel:` at the start of a line  → `NewLabel:`  (transcript prefixes)
 *   2. `\bOldLabel\b` anywhere as a whole word → `NewLabel`  (Summary mentions
 *      like "Speaker 1 reported pain", or `Speaker 2's history` — the word
 *      boundary keeps unrelated text intact and is case-sensitive so prose
 *      like "the doctor said" is left alone).
 */
function replaceSpeakerLabel(input, oldLabel, newLabel) {
  if (typeof input !== 'string') return input;
  if (oldLabel === newLabel) return input;
  const escapedOld = oldLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const lineStartRe = new RegExp('^' + escapedOld + ':', 'gm');
  const wholeWordRe = new RegExp('\\b' + escapedOld + '\\b', 'g');
  return input
    .replace(lineStartRe, SPEAKER_SWAP_SENTINEL + ':')
    .replace(wholeWordRe, SPEAKER_SWAP_SENTINEL)
    .split(SPEAKER_SWAP_SENTINEL).join(newLabel);
}

/**
 * Phase 33 — flatten a page's items into the allowlist-visible items.
 * Walks page-level elements AND group children so the Template variant table
 * shows every element the user might want to accept. Groups are never
 * filtered — only the element's `type` determines allowlist membership
 * (checked downstream in the JSX loop).
 *
 * Pure function, no side effects, order preserved (stable render order =
 * stable row order between re-renders; checkedIds is keyed by element id so
 * reordering mid-flow is safe, but we keep this deterministic for UX).
 *
 * Mirrors the routeTemplateValues allowlist walker in syncToService.js
 * (Phase 33-01) — same flattening rule, different purpose (here: render;
 * there: prompt-build).
 */
function getAllowlistItems(page) {
  const items = [];
  for (const item of (page?.items || [])) {
    if (item?.kind === 'element') items.push(item);
    if (item?.kind === 'group') {
      for (const child of (item?.children || [])) items.push(child);
    }
  }
  return items;
}

/**
 * Phase 33 — flatten existingCapturedValues ({ id: { value, fieldType, capturedAt } })
 * into { id: value } for NoteRenderer's readonlyValues overlay. NoteRenderer
 * expects flat values, NOT the wrapper object — PITFALL #12 invariant.
 */
function flattenExistingCaptured(existing) {
  if (!existing || typeof existing !== 'object') return {};
  const out = {};
  for (const key of Object.keys(existing)) {
    const entry = existing[key];
    if (entry && typeof entry === 'object' && 'value' in entry) {
      out[key] = entry.value;
    } else {
      // Defensive — if shape drifts, still expose whatever value is there.
      out[key] = entry;
    }
  }
  return out;
}

export default function ReviewModal({
  show,
  onClose,
  onApplied,
  mode,
  sessionId,
  // Stage 4 — backend marks the job APPLIED when this is forwarded.
  jobId,
  // Note-variant props (Phase 32 — unchanged)
  cleaned,
  cleanupFailed,
  transcript,
  durationMs,
  // Template-variant props (Phase 33)
  templateId,
  templatePages,
  routedValues,
  dropped,
  existingCapturedValues,
  // v16.00 + sanitised-retry telemetry. Drive the empty-state banner that
  // tells the user up-front *why* the template fields are blank, so they
  // don't dismiss the job thinking the system is broken.
  routingFailed = false,
  routingEmpty = false,
  // The patient this capture belongs to, and the appointment it was recorded
  // at. Together they drive the "file it under" picker below.
  patientId,
  captureAppointmentId = null,
}) {
  // Note-variant state (Phase 32 — unchanged semantics)
  const { toast, showToast, hideToast } = useToast();
  const { confirm, confirmProps } = useConfirm();
  const [text, setText] = useState(cleaned || '');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  // Detected speaker list + current label per speaker. Initialised on modal
  // open from the cleaned content; updated as the user picks Practitioner / Patient
  // from the dropdowns and we string-replace the prefix in the textarea.
  const [speakers, setSpeakers] = useState([]);
  // Phase 34 SC7 — set on 403 'SyncTo is not enabled for this user' from /apply.
  // Swaps the footer to Cancel-only and renders the FEATURE_DISABLED_TEXT banner.
  const [featureDisabled, setFeatureDisabled] = useState(false);
  // Template-variant state (Phase 33).
  // editedValues is the merged state NoteRenderer renders/edits:
  //   { ...sessionExistingValues, ...AIProposedValues }
  // baselineValues is the snapshot of the session's existing values at modal
  // open — used to compute the delta on Apply (any field that differs from
  // baseline is sent to /apply).
  const [editedValues, setEditedValues] = useState({});
  const [baselineValues, setBaselineValues] = useState({});
  const { apply } = useSyncToApi();

  // Where the note will be filed. A recording is regularly made before the
  // visit is booked, or under the wrong booking, so the reviewer chooses and
  // the capture's own appointment is only the default. '' means ad-hoc.
  //
  // Only offered when the capture isn't already bound to a note: once a
  // sessionId exists the note is where it is, and a picker would imply a move
  // that /apply doesn't perform.
  const [appointments, setAppointments] = useState([]);
  const [targetAppointmentId, setTargetAppointmentId] = useState('');
  const canChooseAppointment = !sessionId && !!patientId;

  useEffect(() => {
    if (!show || !canChooseAppointment) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await getAppointmentsByPatientId(patientId);
        if (cancelled) return;
        const sorted = [...(rows || [])].sort(
          (a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0),
        );
        setAppointments(sorted);
      } catch (_e) {
        // The picker degrades to "Ad hoc / the capture's own appointment" —
        // never block an apply because a list couldn't load.
        if (!cancelled) setAppointments([]);
      }
    })();
    return () => { cancelled = true; };
  }, [show, canChooseAppointment, patientId]);

  useEffect(() => {
    if (!show) return;
    setTargetAppointmentId(captureAppointmentId ? String(captureAppointmentId) : '');
  }, [show, captureAppointmentId]);

  // Re-initialise state when the modal re-opens with fresh data.
  useEffect(() => {
    if (!show) return;
    setApplying(false);
    setError(null);
    setAppliedResult(null);
    // Phase 34 SC7 — clear the disabled-banner state on every re-open so a
    // subsequent session (after auth re-validates the flag) gets a clean UI.
    setFeatureDisabled(false);
    // Note variant
    setText(cleaned || '');
    setSpeakers(detectSpeakers(cleaned || ''));
    // Template variant — render the entire template (NoteRenderer fill mode)
    // pre-populated with: existing session values first, then AI-proposed
    // values overlaid on top of those (AI wins for fields it routed). User
    // edits/clears any field they want; Apply sends the diff vs the
    // session's existing values.
    if (mode === 'template') {
      const baseline = flattenExistingCaptured(existingCapturedValues);
      const droppedIds = new Set((dropped || []).map((d) => d && d.elementId).filter(Boolean));
      const aiProposed = {};
      for (const page of (templatePages || [])) {
        for (const item of getAllowlistItems(page)) {
          if (!TEMPLATE_ALLOWLIST_TYPES.includes(item?.type)) continue;
          if (droppedIds.has(item.id)) continue;
          if (routedValues && Object.prototype.hasOwnProperty.call(routedValues, item.id)) {
            const v = routedValues[item.id];
            aiProposed[item.id] = item.type === 'checkbox'
              ? !!v
              : (v === null || v === undefined ? '' : String(v));
          }
        }
      }
      setBaselineValues(baseline);
      setEditedValues({ ...baseline, ...aiProposed });
    } else {
      setEditedValues({});
      setBaselineValues({});
    }
  }, [show, cleaned, mode, templatePages, routedValues, dropped, existingCapturedValues]);

  // Dropdown handler: rename a speaker's prefix throughout the textarea.
  // The `speakers[].currentLabel` tracks what's currently in the text for
  // that speaker — string-replace from old → new and update state.
  const onChangeSpeakerLabel = (speakerNum, newLabel) => {
    setSpeakers((prev) => {
      const idx = prev.findIndex((s) => s.num === speakerNum);
      if (idx === -1) return prev;
      const oldLabel = prev[idx].currentLabel;
      if (oldLabel === newLabel) return prev;
      setText((t) => replaceSpeakerLabel(t, oldLabel, newLabel));
      const next = prev.slice();
      next[idx] = { ...next[idx], currentLabel: newLabel };
      return next;
    });
  };

  const isTemplate = mode === 'template';

  // Compute the delta between editedValues and baselineValues. A field is
  // "changed" if its current edited value differs from what the session had
  // before the modal opened. Treat undefined/null/'' as equivalent for empty
  // string fields so we don't churn no-op writes.
  const computeAcceptedFields = () => {
    const isEmpty = (v) => v === undefined || v === null || v === '';
    const out = {};
    for (const id of Object.keys(editedValues)) {
      const next = editedValues[id];
      const prev = baselineValues[id];
      if (typeof next === 'boolean' || typeof prev === 'boolean') {
        if (!!next !== !!prev) out[id] = next;
      } else if (isEmpty(next) && isEmpty(prev)) {
        continue;
      } else if (next !== prev) {
        out[id] = next;
      }
    }
    return out;
  };
  const changedFieldCount = (() => {
    if (!isTemplate) return 0;
    return Object.keys(computeAcceptedFields()).length;
  })();

  // A capture that was never bound to a note applies via its jobId — /apply
  // creates the note. Either identifier is enough to have somewhere to write.
  const canApply = !!sessionId || !!jobId;

  // What to tell /apply about filing. Omitted entirely when the capture is
  // already bound to a note — the server then keeps its existing behaviour.
  const applyTarget = !canChooseAppointment
    ? {}
    : (targetAppointmentId
        ? { appointmentId: targetAppointmentId }
        : { adHoc: true });

  const onApply = async () => {
    // Template variant — Phase 33
    if (mode === 'template') {
      if (!canApply) return;
      const acceptedFields = computeAcceptedFields();
      if (Object.keys(acceptedFields).length === 0) return;
      // If the session already has captured values, applying SyncTo will
      // overwrite them. Gate the apply on an explicit confirm.
      const hasExistingValues =
        existingCapturedValues &&
        Object.keys(existingCapturedValues).length > 0;
      if (hasExistingValues) {
        const ok = await confirm({
          title: "Overwrite saved session?",
          message:
            "This session already has saved values that will be overwritten.\n\n" +
            "If you want to record a new session, cancel and use \"Add Note\" instead.",
          confirmLabel: "Overwrite",
          cancelLabel: "Cancel",
          confirmVariant: "danger",
        });
        if (!ok) return;
      }
      setApplying(true);
      setError(null);
      const { data, error: err } = await apply({
        sessionId,
        mode: 'template',
        templateId,
        acceptedFields,
        transcript,
        durationMs,
        jobId,
        ...applyTarget,
      });
      setApplying(false);
      if (err) {
        // Phase 34 SC7 — detect the flag-flipped-off-mid-session response.
        if (err.status === FEATURE_DISABLED_STATUS && err.message === FEATURE_DISABLED_MESSAGE) {
          setFeatureDisabled(true);
          setError(null);
          if (typeof onApplied === 'function') onApplied({ featureDisabled: true });
          return;
        }
        setError(err.message || 'Apply failed');
        return;
      }
      setAppliedResult(data);
      if (onApplied) onApplied(data);
      return;
    }
    // Note variant — Phase 32 (unchanged)
    if (!text.trim() || !canApply) return;
    setApplying(true);
    setError(null);
    const { data, error: err } = await apply({
      sessionId,
      mode: mode || 'note',
      cleanedText: text,
      transcript,
      durationMs,
      jobId,
      ...applyTarget,
    });
    setApplying(false);
    if (err) {
      // Phase 34 SC7 — detect the flag-flipped-off-mid-session response.
      if (err.status === FEATURE_DISABLED_STATUS && err.message === FEATURE_DISABLED_MESSAGE) {
        setFeatureDisabled(true);
        setError(null);
        if (typeof onApplied === 'function') onApplied({ featureDisabled: true });
        return;
      }
      setError(err.message || 'Apply failed');
      return;
    }
    setAppliedResult(data);
    if (onApplied) onApplied(data);
  };

  return (
    <>
    <AppToast
      show={toast.show}
      onClose={hideToast}
      variant={toast.variant}
      message={toast.message}
    />
    <ConfirmModal {...confirmProps} />
    <Modal
      show={show}
      onHide={onClose}
      centered
      scrollable
      dialogClassName="modal-85w"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          {mode === 'template' ? 'Review template fields' : 'Review transcript'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cleanupFailed && mode !== 'template' && (
          <Alert variant="warning" className="mb-3">
            {CLEANUP_FAILED_TEXT}
          </Alert>
        )}
        {appliedResult && appliedResult.idempotent && (
          <Alert variant="info" className="mb-3 py-2 small">
            {IDEMPOTENT_TEXT}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {featureDisabled && (
          <Alert variant="warning" className="mb-3" role="alert">
            {FEATURE_DISABLED_TEXT}
          </Alert>
        )}
        {canChooseAppointment && (
          <Form.Group className="mb-3">
            <Form.Label>File this note under</Form.Label>
            <Form.Select
              value={targetAppointmentId}
              onChange={(e) => setTargetAppointmentId(e.target.value)}
              disabled={applying}
              aria-label="Appointment for this note"
            >
              <option value="">Ad hoc — not linked to an appointment</option>
              {/* The endpoint returns startDate / time / status but no service
                  name, so the date IS the discriminator — which is fine for one
                  patient's own bookings. */}
              {appointments.map((a) => (
                <option key={a._id} value={String(a._id)}>
                  {formatAppointmentOption(a)}
                </option>
              ))}
            </Form.Select>
            <span className="small text-muted">
              {captureAppointmentId
                ? 'Defaults to the appointment this was recorded at.'
                : 'This recording was not made at an appointment — pick one if it belongs to a visit.'}
            </span>
          </Form.Group>
        )}

        {mode === 'template' ? (
          <>
            {routingFailed && (
              <Alert variant="warning" className="mb-3">
                <strong>The AI couldn't auto-fill any template fields.</strong>
                {' '}The transcription itself was captured — review or copy it
                from the transcript below, fill the template manually, and
                click <strong>Apply</strong> when ready. Nothing has been
                discarded.
              </Alert>
            )}
            {!routingFailed && routingEmpty && (
              <Alert variant="info" className="mb-3">
                <strong>No template fields matched this recording.</strong>
                {' '}The AI ran successfully but didn't find any content that
                fitted the fields on this template — likely because the
                recording was short, off-topic, or the template uses field
                types the routing doesn't yet support. The raw transcript
                is preserved below.
              </Alert>
            )}
            <p className="small text-muted mb-2">
              The template is pre-filled with the AI's proposed values on top
              of any existing entries. Edit or clear any field you don't want
              to keep, then click <strong>Apply</strong> to save your changes.
            </p>
            <NoteRenderer
              mode="fill"
              pages={templatePages || []}
              values={editedValues}
              onChange={(id, val) =>
                setEditedValues((prev) => ({ ...prev, [id]: val }))
              }
            />
            {(routingFailed || routingEmpty) && (transcript || '').trim() && (
              <div className="mt-4">
                <p className="small text-muted mb-1">
                  <strong>Raw transcript</strong> — copy any content you want
                  to paste into the template fields above.
                </p>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={transcript || ''}
                  readOnly
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  aria-label="Raw transcript (read-only)"
                />
              </div>
            )}
          </>
        ) : (
          <Form.Group>
            <Form.Label className="small text-muted mb-1">
              Assign each speaker, then edit before applying.
            </Form.Label>
            {speakers.length > 0 && (
              <div className="d-flex flex-wrap gap-3 mb-2 align-items-center">
                {speakers.map((s) => {
                  const defaultLabel = `Speaker ${s.num}`;
                  const optionList = SPEAKER_LABEL_OPTIONS.includes(s.currentLabel)
                    ? SPEAKER_LABEL_OPTIONS
                    : [s.currentLabel, ...SPEAKER_LABEL_OPTIONS];
                  return (
                    <div key={s.num} className="d-flex align-items-center gap-2">
                      <span className="small text-muted">{`Speaker ${s.num} is:`}</span>
                      <Form.Select
                        size="sm"
                        value={s.currentLabel}
                        onChange={(e) => onChangeSpeakerLabel(s.num, e.target.value)}
                        disabled={applying}
                        style={{ width: 'auto' }}
                        aria-label={`Role for speaker ${s.num}`}
                      >
                        <option value={defaultLabel}>{defaultLabel}</option>
                        {optionList.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </Form.Select>
                    </div>
                  );
                })}
              </div>
            )}
            <Form.Control
              as="textarea"
              rows={20}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={applying}
              aria-label="Transcript review textarea"
              style={{ minHeight: '60vh', resize: 'vertical' }}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        {mode === 'template' ? (
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-secondary" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            {!featureDisabled && (
              <Button
                variant="primary"
                onClick={onApply}
                disabled={applying || changedFieldCount === 0 || !canApply}
              >
                {applying ? 'Applying\u2026' : `Apply (${changedFieldCount})`}
              </Button>
            )}
          </div>
        ) : (
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-secondary" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            {!featureDisabled && (
              <Button
                variant="primary"
                onClick={onApply}
                disabled={applying || !text.trim() || !canApply}
              >
                {applying ? 'Applying\u2026' : 'Apply'}
              </Button>
            )}
          </div>
        )}
      </Modal.Footer>
    </Modal>
    </>
  );
}
